import React, { useMemo, useRef, useState } from 'react';
import { Box, Typography, Paper, IconButton, Tooltip, Dialog, DialogTitle, DialogContent } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

export interface HelpScreenshot {
  id: string;
  title: string;
  description: string;
  imagePath: string;
  animatedImagePath?: string; // Optional animated GIF path
  altText: string;
  steps: string[];
}

interface HelpScreenshotProps {
  screenshot: HelpScreenshot;
}

const HelpScreenshot: React.FC<HelpScreenshotProps> = ({ screenshot }) => {
  const [showAnimated, setShowAnimated] = useState(false);
  const hasAnimated = !!screenshot.animatedImagePath;
  const [isZoomOpen, setIsZoomOpen] = useState(false);
  const [zoom, setZoom] = useState(1);
  const zoomContainerRef = useRef<HTMLDivElement | null>(null);
  const dragStateRef = useRef<{
    isDragging: boolean;
    startX: number;
    startY: number;
    startScrollLeft: number;
    startScrollTop: number;
  }>({
    isDragging: false,
    startX: 0,
    startY: 0,
    startScrollLeft: 0,
    startScrollTop: 0,
  });

  const currentSrc = showAnimated && hasAnimated ? screenshot.animatedImagePath! : screenshot.imagePath;

  const openZoom = () => {
    setZoom(1);
    setIsZoomOpen(true);
  };

  const clampZoom = (z: number) => Math.min(5, Math.max(0.5, Math.round(z * 100) / 100));
  const zoomIn = () => setZoom((z) => Math.min(5, Math.round((z + 0.25) * 100) / 100));
  const zoomOut = () => setZoom((z) => Math.max(0.5, Math.round((z - 0.25) * 100) / 100));
  const resetZoom = () => setZoom(1);
  const zoomPercentLabel = useMemo(() => `${Math.round(zoom * 100)}%`, [zoom]);

  return (
    <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">
          {screenshot.title}
        </Typography>
        {hasAnimated && (
          <Tooltip title={showAnimated ? "Show static image" : "Show animated demo"}>
            <IconButton 
              size="small" 
              onClick={() => setShowAnimated(!showAnimated)}
              sx={{ color: 'primary.main' }}
            >
              {showAnimated ? <PauseIcon /> : <PlayArrowIcon />}
            </IconButton>
          </Tooltip>
        )}
      </Box>
      
      <Box sx={{ mb: 2 }}>
        <img 
          src={currentSrc} 
          alt={screenshot.altText}
          style={{
            maxWidth: '100%',
            height: 'auto',
            border: '1px solid #ddd',
            borderRadius: '4px',
            minHeight: '50px',
            backgroundColor: '#f0f0f0',
            cursor: 'zoom-in'
          }}
          onClick={openZoom}
          onError={(e) => {
            console.error('Failed to load screenshot:', currentSrc);
            console.error('Error details:', e);
            (e.target as HTMLImageElement).style.display = 'none';
            // Show fallback text
            const fallbackDiv = document.createElement('div');
            fallbackDiv.textContent = `[Screenshot: ${screenshot.title}]`;
            fallbackDiv.style.padding = '20px';
            fallbackDiv.style.backgroundColor = '#f0f0f0';
            fallbackDiv.style.border = '1px solid #ddd';
            fallbackDiv.style.borderRadius = '4px';
            fallbackDiv.style.textAlign = 'center';
            fallbackDiv.style.color = '#666';
            (e.target as HTMLImageElement).parentNode?.insertBefore(fallbackDiv, (e.target as HTMLImageElement).nextSibling);
          }}
          onLoad={(e) => {
            console.log('Screenshot loaded successfully:', currentSrc);
            console.log('Image dimensions:', (e.target as HTMLImageElement).naturalWidth, 'x', (e.target as HTMLImageElement).naturalHeight);
          }}
        />
      </Box>

      <Dialog
        open={isZoomOpen}
        onClose={() => setIsZoomOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Typography variant="subtitle1" sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {screenshot.title}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title="Zoom out">
              <span>
                <IconButton size="small" onClick={zoomOut} disabled={zoom <= 0.5}>
                  <ZoomOutIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Zoom in">
              <span>
                <IconButton size="small" onClick={zoomIn} disabled={zoom >= 5}>
                  <ZoomInIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Reset zoom">
              <IconButton size="small" onClick={resetZoom}>
                <RestartAltIcon />
              </IconButton>
            </Tooltip>
            <Typography variant="caption" sx={{ width: 64, textAlign: 'right' }}>
              {zoomPercentLabel}
            </Typography>
            <Tooltip title="Close">
              <IconButton size="small" onClick={() => setIsZoomOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <Box
            sx={{
              width: '100%',
              height: { xs: '70vh', md: '75vh' },
              overflow: 'auto',
              backgroundColor: 'grey.900',
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'flex-start',
              p: 2,
            }}
            ref={zoomContainerRef}
            onWheel={(e) => {
              // Ctrl/Meta + wheel zoom so normal scrolling still works
              if (!e.ctrlKey && !e.metaKey) return;
              e.preventDefault();
              const step = 0.12;
              setZoom((z) => clampZoom(z + (e.deltaY < 0 ? step : -step)));
            }}
            onMouseDown={(e) => {
              if (e.button !== 0) return;
              const container = zoomContainerRef.current;
              if (!container) return;
              dragStateRef.current = {
                isDragging: true,
                startX: e.clientX,
                startY: e.clientY,
                startScrollLeft: container.scrollLeft,
                startScrollTop: container.scrollTop,
              };
              container.style.cursor = 'grabbing';
            }}
            onMouseLeave={() => {
              const container = zoomContainerRef.current;
              dragStateRef.current.isDragging = false;
              if (container) container.style.cursor = 'grab';
            }}
            onMouseUp={() => {
              const container = zoomContainerRef.current;
              dragStateRef.current.isDragging = false;
              if (container) container.style.cursor = 'grab';
            }}
            onMouseMove={(e) => {
              const container = zoomContainerRef.current;
              if (!container) return;
              const state = dragStateRef.current;
              if (!state.isDragging) return;
              const dx = e.clientX - state.startX;
              const dy = e.clientY - state.startY;
              container.scrollLeft = state.startScrollLeft - dx;
              container.scrollTop = state.startScrollTop - dy;
            }}
            style={{ cursor: 'grab' }}
          >
            <Box
              component="img"
              src={currentSrc}
              alt={screenshot.altText}
              sx={{
                transform: `scale(${zoom})`,
                transformOrigin: 'top left',
                borderRadius: 1,
                backgroundColor: 'grey.800',
                boxShadow: 3,
                userSelect: 'none',
                pointerEvents: 'none',
              }}
            />
          </Box>
        </DialogContent>
      </Dialog>
      
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {screenshot.description}
      </Typography>
      
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Steps:
        </Typography>
        {screenshot.steps.map((step, index) => (
          <Typography key={index} variant="body2" sx={{ mb: 1 }}>
            {index + 1}. {step}
          </Typography>
        ))}
      </Box>
    </Paper>
  );
};

export default HelpScreenshot; 